import { Button } from '@components/Button';
import ContactAdvisor from '@components/ContactAdvisor';
import { FavoriteButton } from '@components/MiscComponents/FavoriteButton';
import { Close } from '@icon/Close';
import usePropertySaver from '@util/customHooks/usePropertySaver';
import { generateListTracEvent } from '@util/ListTrac';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import {
  Address,
  AddressWrapper,
  Bar,
  ButtonMobile,
  ContactWrapper,
  DropDown,
  Header,
  HeaderWrapper,
  MobileButtonWrapper,
  MobileDropDown,
  MobileIconButtonsWrapper,
  MobileShare,
  MobileShareWrapper,
  PricedFromText,
  SaveButtonContainer,
  ScreenDiv,
  ShareCloseIcon,
  ShareContainer,
  ShareLable,
  ShareWrapper,
  StyledFavoriteButtonText,
  Wrapper,
} from './styles';

export interface ShareHeaderProps {
  cost?: string;
  address?: string;
  url?: string;
  listingId: string;
  listOfficeMlsId: string;
  listAgentFullName?: string;
  listOfficeName?: string;
  originatingSystemName?: string;
  listAgentMlsId?: string;
  zipCode: string;
  isPropertyDataLoaded?: boolean;
  pageType?: string;
  projectWebsite?: string;
  startingFrom?: string;
  isCooperativeAgency?: boolean;
}

const ShareHeader: FC<ShareHeaderProps> = ({
  cost,
  address,
  url,
  listAgentFullName,
  listOfficeName,
  listingId,
  listOfficeMlsId,
  originatingSystemName,
  listAgentMlsId,
  zipCode,
  pageType,
  projectWebsite,
  startingFrom,
  isCooperativeAgency,
}) => {
  const t = useTranslations();
  const [_pageType, setPageType] = useState(pageType);
  const [isOpen, setOpen] = useState(false);
  const toggle = () => setOpen(!isOpen);
  const [isModalOpen, setModalOpen] = useState(false);
  const modalToggle = () => setModalOpen(!isModalOpen);
  const hideElements = address === null && cost == '0' ? true : false;
  const { isFav, onSaveProperty } = usePropertySaver({ listingId, listOfficeMlsId }); // Function for favoriting a property
  const [shareDetails, setShareDetails] = useState({
    link: '',
    subject: '',
    body: '',
  });

  useEffect(() => {
    setPageType(pageType);
  }, [pageType]);

  useEffect(() => {
    setShareDetails({
      link: encodeURI(document?.location?.href),
      subject: encodeURI('Check Out This Property!'),
      body: encodeURI(
        `Check out this property I found on evrealestate.com \n${document?.location?.href}`,
      ),
    });
  }, []);

  const getSaveButtonText = (isFavorite) => {
    if (isFavorite) {
      return t('Saved');
    } else {
      return t('Save');
    }
  };

  const handleSaveProperty = async () => {
    onSaveProperty();
    await generateListTracEvent({ type: 'favorite', listingId, zipCode });
  };

  let displayCost;

  const developmentDetailPageWithStartingFrom =
    pageType === 'developmentDetail' && startingFrom && startingFrom != '0';

  if (pageType === 'developmentDetail') {
    if (startingFrom && startingFrom != '0') {
      displayCost = startingFrom;
    }
  } else if (cost && cost != '0' && !cost.includes('$0')) {
    displayCost = cost;
  }

  const visitWebsiteUrl = projectWebsite?.replace('http://', '')?.replace('https://', '');

  return (
    <HeaderWrapper $isModalOpen={isModalOpen}>
      <Wrapper style={{ opacity: hideElements ? '0' : '1' }}>
        {displayCost && (
          <>
            <Header data-testid="listing-price">
              {developmentDetailPageWithStartingFrom ? (
                <div style={{ height: '4rem' }}>
                  <PricedFromText>Priced from</PricedFromText>
                  {displayCost}
                </div>
              ) : (
                <>{displayCost}</>
              )}
            </Header>
            <Bar />
          </>
        )}
        {address && (
          <AddressWrapper
            style={
              developmentDetailPageWithStartingFrom
                ? {
                    height: '4rem',
                    justifyContent: 'space-between',
                  }
                : { height: '3.5rem', justifyContent: 'normal' }
            }
          >
            <Address data-testid="listing-address">{address}</Address>
            <ShareWrapper>
              <SaveButtonContainer>
                <FavoriteButton
                  $isFavorite={isFav ?? undefined}
                  dataTestId="listing-details-save-button"
                  onClick={() => handleSaveProperty()}
                />
                <StyledFavoriteButtonText>{getSaveButtonText(isFav)}</StyledFavoriteButtonText>
              </SaveButtonContainer>
              <ShareContainer onClick={toggle}>
                <Button
                  dataTestId="listing-details-share-button"
                  variant="tertiaryGray"
                  label={t('Share')}
                  arrow={false}
                  icon={'Share'}
                  iconPosition="left"
                  url={'#'}
                  disabled={url === '#'}
                />
                {isOpen && (
                  <DropDown>
                    <a
                      href={`sms:?&body=${shareDetails?.link}`}
                      onClick={async () => {
                        await generateListTracEvent({
                          type: 'share',
                          listingId,
                          zipCode,
                        });
                      }}
                    >
                      {t('Message')}
                    </a>
                    <a
                      href={`mailto:?subject=${shareDetails?.subject}&body=${shareDetails?.body}`}
                      onClick={async () => {
                        await generateListTracEvent({
                          type: 'share',
                          listingId,
                          zipCode,
                        });
                      }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('Email')}
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer.php?u=${shareDetails?.link}`}
                      onClick={async () => {
                        await generateListTracEvent({
                          type: 'share',
                          listingId,
                          zipCode,
                        });
                      }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Facebook
                    </a>
                  </DropDown>
                )}
              </ShareContainer>
            </ShareWrapper>
          </AddressWrapper>
        )}
      </Wrapper>
      <ContactWrapper>
        <ButtonMobile>
          {_pageType === 'developmentDetail' && !visitWebsiteUrl ? (
            <></>
          ) : _pageType === 'developmentDetail' && visitWebsiteUrl ? (
            <Button
              arrow={false}
              variant={'primaryRed'}
              onClickHandler={() => {
                Object.assign(document.createElement('a'), {
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  href: `https://${visitWebsiteUrl}`,
                }).click();
              }}
              label={t('Visit Website')}
              dataTestId="visit-website-button"
            />
          ) : (
            <ContactAdvisor
              handleToggle={modalToggle}
              overrideText={t('Contact an Advisor')}
              listAgentFullName={listAgentFullName}
              listOfficeName={listOfficeName}
              originatingSystemName={originatingSystemName}
              listAgentMlsId={listAgentMlsId}
              zipCode={zipCode}
              listingId={listingId}
              listOfficeMlsId={listOfficeMlsId}
              isCooperativeAgency={isCooperativeAgency}
            />
          )}
        </ButtonMobile>
        <MobileIconButtonsWrapper>
          <MobileButtonWrapper $isFavorite={isFav ?? undefined}>
            <FavoriteButton $isFavorite={isFav ?? undefined} onClick={() => handleSaveProperty()} />
          </MobileButtonWrapper>
          {isOpen && <ScreenDiv onClick={toggle} />}
          <MobileShare onClick={toggle}>
            <Button
              variant="tertiary"
              label="Share"
              arrow={false}
              icon={'Share'}
              iconPosition="left"
              url={'#'}
              disabled={url === '#'}
            />
            {isOpen && (
              <MobileDropDown>
                <MobileShareWrapper>
                  <ShareLable>{t('Share')}</ShareLable>
                  <ShareCloseIcon>
                    <Close />
                  </ShareCloseIcon>
                </MobileShareWrapper>
                <a href={`sms:?&body=${shareDetails.link}`}>{t('Message')}</a>
                <a
                  href={`mailto:?subject=${shareDetails.subject}&body=${shareDetails.body}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('Share by Email')}
                </a>
                <a
                  href={`https://www.facebook.com/sharer.php?u=${shareDetails.link}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Facebook
                </a>
              </MobileDropDown>
            )}
          </MobileShare>
        </MobileIconButtonsWrapper>
      </ContactWrapper>
    </HeaderWrapper>
  );
};

export default ShareHeader;
